import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CameraPreset, CameraAnimationState } from '@/types/camera.types';

/**
 * Camera controller for managing camera animations and orbit controls
 */
export class CameraController {
  private camera: THREE.PerspectiveCamera;
  private controls: OrbitControls;
  private animationState: CameraAnimationState;

  constructor(camera: THREE.PerspectiveCamera, controls: OrbitControls) {
    this.camera = camera;
    this.controls = controls;

    // Initialize animation state
    this.animationState = {
      isAnimating: false,
      progress: 0,
      startPosition: new THREE.Vector3(),
      endPosition: new THREE.Vector3(),
      startTarget: new THREE.Vector3(),
      endTarget: new THREE.Vector3(),
      duration: 3,
    };
  }

  /**
   * Ease in-out cubic function for smooth camera movement
   */
  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /**
   * Start animation to a new camera preset
   * @param preset Target camera preset
   * @param duration Animation duration in seconds
   */
  public animateTo(preset: CameraPreset, duration: number = 3): void {
    this.animationState.startPosition.copy(this.camera.position);
    this.animationState.endPosition.set(
      preset.position.x,
      preset.position.y,
      preset.position.z
    );
    this.animationState.startTarget.copy(this.controls.target);
    this.animationState.endTarget.set(
      preset.target.x,
      preset.target.y,
      preset.target.z
    );
    this.animationState.duration = duration;
    this.animationState.progress = 0;
    this.animationState.isAnimating = true;

    // Disable user controls during animation
    this.controls.enabled = false;
  }

  /**
   * Update camera animation (call in animation loop)
   * @param deltaTime Time since last frame in seconds
   */
  public update(deltaTime: number): void {
    if (!this.animationState.isAnimating) {
      // Always update controls even when not animating
      this.controls.update();
      return;
    }

    this.animationState.progress += deltaTime / this.animationState.duration;

    if (this.animationState.progress >= 1) {
      this.animationState.progress = 1;
      this.animationState.isAnimating = false;
      // Re-enable user controls after animation
      this.controls.enabled = true;
    }

    // Apply easing
    const easedProgress = this.easeInOutCubic(this.animationState.progress);

    // Interpolate camera position
    this.camera.position.lerpVectors(
      this.animationState.startPosition,
      this.animationState.endPosition,
      easedProgress
    );

    // Interpolate look-at target
    this.controls.target.lerpVectors(
      this.animationState.startTarget,
      this.animationState.endTarget,
      easedProgress
    );

    this.controls.update();
  }

  /**
   * Set camera to a preset immediately (no animation)
   */
  public setPreset(preset: CameraPreset): void {
    this.camera.position.set(
      preset.position.x,
      preset.position.y,
      preset.position.z
    );
    this.controls.target.set(
      preset.target.x,
      preset.target.y,
      preset.target.z
    );
    this.controls.update();
  }

  /**
   * Check if camera is currently animating
   */
  public isAnimating(): boolean {
    return this.animationState.isAnimating;
  }

  /**
   * Enable or disable orbit controls
   */
  public setControlsEnabled(enabled: boolean): void {
    this.controls.enabled = enabled;
  }
}
